import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, Database, Eye, EyeOff, Image, Play, RefreshCw, Server, Square, Timer } from "lucide-react";
import api from "Common/api";
import "./mls-cache-control.css";

type SyncLog = {
  id: number;
  sync_type: string;
  status: string;
  cutoff_date: string | null;
  fetched_count: number;
  updated_count: number;
  photo_count: number;
  error_text: string | null;
  started_at: string;
  finished_at: string | null;
};

type SyncForm = {
  days: string;
  limit: string;
  photoLimit: string;
  maxBatches: string;
};

type CacheStats = {
  totalProperties: number;
  activeProperties: number;
  totalPhotos: number;
  listingsWithPhotos: number;
  runningSyncs: number;
  lastPropertySyncedAt: string | null;
  latestSync: SyncLog | null;
};

const tokenStorageKey = "mls_cache_admin_token";

function formatDate(value?: string | null) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatNumber(value?: number | string | null) {
  const numeric = Number(value || 0);
  return numeric.toLocaleString();
}

function formatDuration(start?: string | null, end?: string | null) {
  if (!start) return "Not running";
  const startMs = new Date(start).getTime();
  const endMs = end ? new Date(end).getTime() : Date.now();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return "Not available";

  const totalSeconds = Math.max(Math.floor((endMs - startMs) / 1000), 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  }

  return `${minutes}m ${seconds}s`;
}

function StatusBadge({ status }: { status?: string }) {
  const normalized = (status || "unknown").toLowerCase();
  const className =
    normalized === "success"
      ? "badge bg-success-subtle text-success"
      : normalized === "failed"
        ? "badge bg-danger-subtle text-danger"
        : normalized === "running"
          ? "badge bg-warning-subtle text-warning"
          : "badge bg-secondary-subtle text-secondary";

  return <span className={className}>{normalized}</span>;
}

export default function MlsCacheControl() {
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [stats, setStats] = useState<CacheStats>({
    totalProperties: 0,
    activeProperties: 0,
    totalPhotos: 0,
    listingsWithPhotos: 0,
    runningSyncs: 0,
    lastPropertySyncedAt: null,
    latestSync: null,
  });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem(tokenStorageKey) || "");
  const [showToken, setShowToken] = useState(false);
  const [form, setForm] = useState<SyncForm>({
    days: "730",
    limit: "500",
    photoLimit: "1",
    maxBatches: "",
  });

  const latestLog = logs[0];
  const runningLog = useMemo(
    () => logs.find((log) => log.status === "running"),
    [logs]
  );

  const loadStatus = async () => {
    setLoading(true);
    setError("");
    try {
      const [statusResp, propertiesResp] = await Promise.all([
        api.get("/mls-cache/sync/status"),
        api.get("/mls-cache/stats"),
      ]);

      setLogs((statusResp as any)?.logs || []);
      setStats((propertiesResp as any)?.stats || {
        totalProperties: 0,
        activeProperties: 0,
        totalPhotos: 0,
        listingsWithPhotos: 0,
        runningSyncs: 0,
        lastPropertySyncedAt: null,
        latestSync: null,
      });
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Unable to load MLS cache status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  useEffect(() => {
    if (!runningLog && stats.runningSyncs === 0) return;

    const interval = window.setInterval(() => {
      loadStatus();
    }, 5000);

    return () => window.clearInterval(interval);
  }, [runningLog?.id, stats.runningSyncs]);

  const updateForm = (key: keyof SyncForm, value: string) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveToken = () => {
    localStorage.setItem(tokenStorageKey, adminToken.trim());
    setMessage("Sync token saved in this browser.");
    setError("");
  };

  const runSync = async (preset?: Partial<SyncForm>) => {
    setSyncing(true);
    setMessage("");
    setError("");

    const payload = {
      ...form,
      ...preset,
    };

    const params = new URLSearchParams();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        params.append(key, String(value).trim());
      }
    });

    try {
      const result = await api.post(
        `/mls-cache/sync/run?${params.toString()}`,
        {},
        {
          headers: adminToken.trim()
            ? {
                "x-mls-cache-token": adminToken.trim(),
              }
            : undefined,
        }
      );

      setMessage(
        `Sync completed: ${formatNumber((result as any)?.fetchedCount)} fetched, ${formatNumber(
          (result as any)?.updatedCount
        )} updated, ${formatNumber((result as any)?.photoCount)} photos.`
      );
      await loadStatus();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "MLS sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const requestHeaders = adminToken.trim()
    ? {
        "x-mls-cache-token": adminToken.trim(),
      }
    : undefined;

  const stopSync = async () => {
    setStopping(true);
    setMessage("");
    setError("");

    try {
      const result = await api.post(
        "/mls-cache/sync/stop",
        {},
        {
          headers: requestHeaders,
        }
      );
      setMessage(`Stop requested. ${formatNumber((result as any)?.stoppedCount)} running sync(s) marked stopped.`);
      await loadStatus();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "Stop sync failed");
    } finally {
      setStopping(false);
    }
  };

  const syncMissingPhotos = async () => {
    setSyncing(true);
    setMessage("");
    setError("");

    const params = new URLSearchParams();
    params.append("limit", form.limit || "50");
    params.append("photoLimit", form.photoLimit || "1");
    params.append("maxBatches", form.maxBatches || "1");
    params.append("photoConcurrency", "1");
    params.append("photoDelayMs", "1000");

    try {
      const result = await api.post(
        `/mls-cache/sync/photos/missing?${params.toString()}`,
        {},
        {
          headers: requestHeaders,
        }
      );
      setMessage(
        `Missing photo sync completed: ${formatNumber((result as any)?.fetchedCount)} listings checked, ${formatNumber(
          (result as any)?.photoCount
        )} photos saved.`
      );
      await loadStatus();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.response?.data?.error || err?.message || "Missing photo sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const statCards = [
    {
      label: "Imported Properties",
      value: formatNumber(stats.totalProperties),
      icon: <Database size={22} />,
    },
    {
      label: "Active Properties",
      value: formatNumber(stats.activeProperties),
      icon: <CheckCircle2 size={22} />,
    },
    {
      label: "Imported Photos",
      value: `${formatNumber(stats.totalPhotos)} / ${formatNumber(stats.listingsWithPhotos)} listings`,
      icon: <Server size={22} />,
    },
    {
      label: runningLog ? "Running For" : "Last Sync",
      value: runningLog ? formatDuration(runningLog.started_at) : latestLog ? formatDate(latestLog.started_at) : "No sync yet",
      icon: <Timer size={22} />,
    },
    {
      label: "Last Status",
      value: latestLog?.status
        ? `${latestLog.status.charAt(0).toUpperCase()}${latestLog.status.slice(1).toLowerCase()}`
        : "Unknown",
      icon: latestLog?.status === "success" ? <CheckCircle2 size={22} /> : <AlertCircle size={22} />,
    },
  ];

  return (
    <div className="page-content mls-cache-page">
      <div className="container-fluid mls-cache-shell">
        <div className="mls-cache-toolbar">
          <div className="mls-cache-intro">
            <p>Monitor RETS sync health and run controlled cache imports.</p>
            {runningLog && (
              <div className="mls-running-note">
                Sync #{runningLog.id} is running. Counts refresh automatically every 5 seconds.
              </div>
            )}
          </div>
          <button className="btn mls-refresh-button d-inline-flex align-items-center gap-2" onClick={loadStatus} disabled={loading}>
            <RefreshCw size={16} className={loading ? "spinner-border spinner-border-sm border-0" : ""} />
            Refresh
          </button>
        </div>

        {message && <div className="alert alert-success">{message}</div>}
        {error && <div className="alert alert-danger">{error}</div>}

        <div className="mls-stat-grid">
          {statCards.map((card) => (
            <div className="mls-stat-card" key={card.label}>
                <div className="d-flex align-items-center gap-3">
                  <div className="mls-stat-icon d-flex align-items-center justify-content-center">
                    {card.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="mls-stat-label">{card.label}</div>
                    <div className="mls-stat-value text-truncate">{card.value}</div>
                  </div>
                </div>
            </div>
          ))}
        </div>

        <div className="mls-workspace-grid">
          <div>
            <div className="card mls-sync-card">
              <div className="card-header">
                <h5 className="card-title mb-0">Manual Sync</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Admin Sync Token</label>
                  <div className="mls-field-help">Stored only in this browser and sent with administrative sync requests.</div>
                  <div className="input-group">
                    <input
                      className="form-control"
                      type={showToken ? "text" : "password"}
                      value={adminToken}
                      onChange={(event) => setAdminToken(event.target.value)}
                      placeholder="x-mls-cache-token"
                    />
                    <button
                      className="btn mls-token-visibility"
                      type="button"
                      onClick={() => setShowToken((visible) => !visible)}
                      aria-label={showToken ? "Hide sync token" : "Show sync token"}
                    >
                      {showToken ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                    <button className="btn btn-outline-secondary" type="button" onClick={saveToken}>
                      Save
                    </button>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-12 col-sm-6">
                    <label className="form-label">Days</label>
                    <input className="form-control" type="number" min="1" value={form.days} onChange={(event) => updateForm("days", event.target.value)} />
                    <div className="mls-field-help">History window</div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label">Batch Limit</label>
                    <input className="form-control" type="number" min="1" value={form.limit} onChange={(event) => updateForm("limit", event.target.value)} />
                    <div className="mls-field-help">Listings per request</div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label">Photos / Listing</label>
                    <input className="form-control" type="number" min="0" value={form.photoLimit} onChange={(event) => updateForm("photoLimit", event.target.value)} />
                    <div className="mls-field-help">Maximum saved per listing</div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <label className="form-label">Max Batches</label>
                    <input className="form-control" type="number" min="1" value={form.maxBatches} onChange={(event) => updateForm("maxBatches", event.target.value)} placeholder="Unlimited" />
                    <div className="mls-field-help">Blank means no limit</div>
                  </div>
                </div>

                <div className="mls-sync-actions">
                  <button
                    className="btn btn-primary mls-primary-sync d-inline-flex align-items-center justify-content-center gap-2"
                    disabled={syncing || Boolean(runningLog) || stats.runningSyncs > 0}
                    onClick={() => runSync()}
                  >
                    {syncing ? <span className="spinner-border spinner-border-sm" /> : <Play size={16} />}
                    Run Configured Sync
                  </button>
                  <div className="mls-secondary-actions">
                  <button
                    className="btn btn-outline-secondary d-inline-flex align-items-center justify-content-center gap-2"
                    disabled={syncing || Boolean(runningLog) || stats.runningSyncs > 0}
                    onClick={() => runSync({ days: "1", limit: "50", photoLimit: "1", maxBatches: "1" })}
                  >
                    <Play size={16} />
                    Run Test Sync
                  </button>
                  <button
                    className="btn btn-outline-primary d-inline-flex align-items-center justify-content-center gap-2"
                    disabled={syncing || Boolean(runningLog) || stats.runningSyncs > 0}
                    onClick={syncMissingPhotos}
                  >
                    {syncing ? <span className="spinner-border spinner-border-sm" /> : <Image size={16} />}
                    Sync Missing Photos
                  </button>
                  </div>
                  {(Boolean(runningLog) || stats.runningSyncs > 0) && (
                    <button
                      className="btn btn-outline-danger d-inline-flex align-items-center justify-content-center gap-2"
                      disabled={stopping}
                      onClick={stopSync}
                    >
                      {stopping ? <span className="spinner-border spinner-border-sm" /> : <Square size={16} />}
                      Stop Running Sync
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="card mls-logs-card">
              <div className="card-header d-flex align-items-center justify-content-between">
                <h5 className="card-title mb-0">Recent Sync Logs</h5>
                <span className="text-muted small">Latest 10 runs</span>
              </div>
              <div className="card-body p-0">
                {logs.length === 0 ? (
                  <div className="mls-empty-logs">
                    <Server size={28} aria-hidden="true" />
                    <h6>No sync activity yet</h6>
                    <p>Run a test or configured sync to see fetched, updated, photo, and error details here.</p>
                    <button className="btn btn-outline-primary btn-sm" onClick={loadStatus} disabled={loading}>
                      <RefreshCw size={14} /> Refresh status
                    </button>
                  </div>
                ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Fetched</th>
                        <th>Updated</th>
                        <th>Photos</th>
                        <th>Started</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                          <tr key={log.id}>
                            <td>{log.id}</td>
                            <td><StatusBadge status={log.status} /></td>
                            <td>{log.sync_type}</td>
                            <td>{formatNumber(log.fetched_count)}</td>
                            <td>{formatNumber(log.updated_count)}</td>
                            <td>{formatNumber(log.photo_count)}</td>
                            <td>{formatDate(log.started_at)}</td>
                            <td className="text-truncate" style={{ maxWidth: 220 }} title={log.error_text || ""}>
                              {log.error_text || "-"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
